import baustein from "baustein";
import browser from "web/script/utils/browser";
import _ from "underscore";
import analytics from "web/script/analytics/analytics";
import { addVisibilityWatcher } from "web/script/modules/run-loop";
import { lowerPriceBound, upperPriceBound, formatData } from "./price-history-graph-utils";

const RETAILER_TAB = "tab-switcher__tab";
const RETAILER_TAB_ACTIVE = "tab-switcher__tab--active";
const RETAILER_TAB_ACTIVE_SELECTOR = `.${RETAILER_TAB_ACTIVE}`;
const CHART_SELECTOR = ".price-history-graph__chart";
const DAY_IN_MILLISECONDS = 1000 * 60 * 60 * 24;

let d3;

/**
 * Calculate the number of days between two dates
 * @param startDate {Date}
 * @param endDate {Date}
 * @returns {Number}
 */
function daysDifference(startDate, endDate) {
    return Math.round((endDate - startDate) / DAY_IN_MILLISECONDS);
}

/**
 * Calculate a date in the future by a given percentage
 * relative to difference of two dates
 * @param startDate {Date}
 * @param endDate {Date}
 * @param percent {Number}
 * @returns {Date}
 */
function calculateFutureDate(startDate, endDate, percent) {
    let numdays = daysDifference(startDate, endDate);
    let daysAhead = 0;

    // Any less will draw graph incorrectly
    if (numdays <= 3) {
        daysAhead = 1;
    } else {
        daysAhead = Math.floor(numdays * (percent / 100));
    }

    let futureDate = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate() + daysAhead
    );

    return futureDate;
}

/**
 * Splits the array of price changes into separate arrays according to in stock and out of stock status
 * @param history {array} - price change objects
 * @returns {Object}
 */
function splitByStockStatus(history) {
    let inStockPeriods = [];
    let outStockPeriods = [];

    let oosIndices = history.reduce(function(acc, priceChange, index) {
        if (priceChange.isOOS) {
            acc.push(index);
        }
        return acc;
    }, []);

    if (oosIndices.length) {
        // If the history contains out of stock changes then the graph needs to be drawn
        // in separate sections
        // Here we iterate over the indices of OOS changes in the history to group price chages
        // into in stock and out of stock time periods

        let startIndex = 0;

        // groups each OOS change with the next IS change
        oosIndices.forEach((index) => {
            let period = history.slice(index, index + 2);

            outStockPeriods.push(period);
        });

        oosIndices.push(history.length - 1);

        // groups each IS change with the next OOS change
        oosIndices.forEach((index) => {
            let period = history.slice(startIndex, index + 1);
            startIndex = index + 1;
            inStockPeriods.push(period);
        });
    } else {
        // if there are no OOS entries just return all the entries
        inStockPeriods.push(history);
    }

    return {
        inStockPeriods,
        outStockPeriods,
    };
}

/**
 * Generate a number of dates for a given number of ticks
 * @param startDate {Date}
 * @param endDate {Date}
 * @param ticks {Number}
 * @returns {Array}
 */
function generateXTicks(startDate, endDate, ticks) {
    let numDays = daysDifference(startDate, endDate);
    let cycle = Math.floor(numDays / ticks);
    let tickValues = [startDate];

    for (let i = 1; i <= ticks; i++) {
        let d = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate() + cycle * i
        );
        tickValues.push(d);
    }

    tickValues.push(endDate);

    return tickValues;
}

/**
 * Generate a prices for y axis
 * @param minPrice {Number}
 * @param maxPrice {Number}
 * @returns {Array}
 */
function generateYTicks(minPrice, maxPrice) {
    let diff = Math.round((maxPrice + minPrice) / 2);

    return [minPrice, diff, maxPrice];
}

/**
 * Formatting for ticks on mobile break point
 */
function mobileTicks(d) {
    return d.toLocaleString("en-us", { month: "short", day: "numeric" });
}

/**
 * Formatting for ticks on tablet upwards, essentially draws ablank for every second tick
 */
function tabletTicks(d, i) {
    if (i === 0 || i % 2 === 0) {
        return d.toLocaleString("en-us", { month: "short", day: "numeric" });
    }
    return "";
}

export default baustein.register("price-history-graph", {
    createTabs() {
        var tabContainer = this.el.querySelector(".tab-switcher__tabs");
        var tabs = "";
        var keys = Object.keys(this.data);

        for (let i = 0; i < keys.length; i++) {
            tabs += `
                <li class="${RETAILER_TAB} ${i == 0 && RETAILER_TAB_ACTIVE}" data-retailer="${
                keys[i]
            }">
                    ${keys[i]}
                </li>
            `;
        }

        if (keys.length === 1) {
            tabContainer.style.display = "none";
        }

        tabContainer.innerHTML = tabs;
    },

    _sendEvent(name) {
        analytics.event("price-history", "price-history-graph", name, true);
    },

    onInsert() {
        this._removeVisiblityWatcher = addVisibilityWatcher(this.el, (visible) => {
            if (visible) {
                this._removeVisiblityWatcher();
                this._sendEvent("price-history-graph-viewed");
            }
        });
    },

    onTabClick(e) {
        this.currentTab.classList.remove(RETAILER_TAB_ACTIVE);
        this.currentTab = e.target;
        this.currentTab.classList.add(RETAILER_TAB_ACTIVE);

        this.chart.innerHTML = "";
        this.drawGraph(this.currentTab.dataset.retailer);
    },

    onResize() {
        this.chart.innerHTML = "";
        this.cbr = this.chart.getBoundingClientRect();
        this.width = this.cbr.width - this.margin.left - this.margin.right;
        this.height = this.cbr.height - this.margin.top - this.margin.bottom;
        this.drawGraph();
    },

    drawLegend() {
        let legendMarkUp = `
            <div class="price-history-graph__legend">
                <div class="price-history-graph__stock-status price-history-graph__stock-status--in-stock"> In Stock </div>
                <div class="price-history-graph__stock-status price-history-graph__stock-status--out-stock"> Out of Stock </div>
            </div>
        `;

        if (this.isTabletUp) {
            this.chart.insertAdjacentHTML("afterbegin", legendMarkUp);
        } else {
            this.chart.insertAdjacentHTML("beforeend", legendMarkUp);
        }
    },

    drawPrice() {
        let priceText;

        if (this.isCurrentlySoldOut) {
            priceText = `
                <div class="price-history-graph__retailer-text">
                    <div class="price-history-graph__price-text price-history-graph__price-text--oos">
                        Sold Out
                    </div>
                </div>`;
        } else {
            priceText = `
                <div class="price-history-graph__retailer-text">
                    Today's price on
                    <span class="price-history-graph__retailer-name">
                        ${this.currentTab.dataset.retailer}
                    </span>
                    <div class="price-history-graph__price-text">
                        ${this.options.currencySymbol}${Math.floor(this.end.price)}
                    </div>
                </div>`;
        }

        this.chart.insertAdjacentHTML("afterbegin", priceText);

        if (this.isTabletUp) {
            let text = this.chart.querySelector(".price-history-graph__retailer-text");
            let leftOffset = this.xScale(this.end.date);
            text.style.left = leftOffset + this.margin.left - 100 + "px";
        }
    },

    drawLines() {
        const line = d3
            .line()
            .x((d) => this.xScale(d.date))
            .y((d) => this.yScale(d.price))
            .curve(d3.curveStepAfter);

        const area = d3
            .area()
            .x((d) => this.xScale(d.date))
            .y0(this.yScale(this.yScale.domain()[0]))
            .y1((d) => this.yScale(d.price))
            .curve(d3.curveStepAfter);

        let eras = splitByStockStatus(this.retailerData);

        eras.inStockPeriods.forEach((era) => {
            this.svg
                .append("path")
                .data([era])
                .attr("class", "price-history-graph__is-area")
                .attr("d", area);

            this.svg
                .append("path")
                .data([era])
                .attr("class", "price-history-graph__is-line")
                .attr("d", line)
                .attr("stroke-linecap", "round");
        });

        eras.outStockPeriods.forEach((era) => {
            this.svg
                .append("path")
                .data([era])
                .attr("class", "price-history-graph__oos-area")
                .attr("d", area);

            this.svg
                .append("path")
                .data([era])
                .attr("class", "price-history-graph__oos-line")
                .attr("d", line)
                .attr("stroke-linecap", "round");
        });
    },

    drawPriceDot() {
        if (this.isCurrentlySoldOut) {
            this.svg
                .append("circle")
                .attr("cx", this.xScale(this.end.date))
                .attr("cy", this.yScale(this.end.price))
                .attr("r", 8)
                .attr(
                    "transform-origin",
                    `${this.xScale(this.end.date)} ${this.yScale(this.end.price)}`
                )
                .attr("class", "price-history-graph__circle-sold-out");
        } else {
            for (let i = 1; i <= 3; i++) {
                this.svg
                    .append("circle")
                    .attr("cx", this.xScale(this.end.date))
                    .attr("cy", this.yScale(this.end.price))
                    .attr("r", 8)
                    .attr(
                        "transform-origin",
                        `${this.xScale(this.end.date)} ${this.yScale(this.end.price)}`
                    )
                    .attr("class", `price-history-graph__circle-${i}`);
            }
        }
    },

    drawAdditionalElements() {
        let selfYScale = this.yScale;
        let futureOOSClass = this.isCurrentlySoldOut ? "price-history-graph__future-line--oos" : "";
        function yGridlines() {
            return d3.axisLeft(selfYScale).ticks(5);
        }

        this.svg
            .append("line")
            .attr("class", "price-history-graph__price-line")
            .attr("x1", this.xScale(this.end.date))
            .attr("y1", this.yScale(this.minVal))
            .attr("x2", this.xScale(this.end.date))
            .attr("y2", this.yScale(this.maxVal));

        this.svg
            .append("line")
            .attr("class", `price-history-graph__future-line ${futureOOSClass}`)
            .attr("x1", this.xScale(this.end.date))
            .attr("y1", this.yScale(this.end.price))
            .attr("x2", this.xScale(this.futureDate))
            .attr("y2", this.yScale(this.end.price));

        this.svg
            .append("g")
            .attr("class", "grid")
            .call(
                yGridlines()
                    .tickSize(-this.width)
                    .tickFormat("")
            );
    },

    drawGraph() {
        const cushion = 50;
        this.margin = { top: 20, right: 30, bottom: 30, left: 50 };
        this.minVal = lowerPriceBound(this.options.minPrice, cushion);
        this.maxVal = upperPriceBound(this.options.maxPrice, cushion);
        this.retailerData = this.data[this.currentTab.dataset.retailer];
        this.start = this.retailerData[0];
        this.end = this.retailerData[this.retailerData.length - 1];
        this.futureDate = this.end.date;
        this.ticks = generateXTicks(this.start.date, this.futureDate, 0);
        this.tickFormatting = mobileTicks;
        this.yOffset = 0;
        this.isTabletUp = browser.getWindowWidth() > 576;
        this.isCurrentlySoldOut = this.end.isOOS;

        if (this.isTabletUp) {
            this.futureDate = calculateFutureDate(this.start.date, this.futureDate, 30);
            this.ticks = null;
            this.tickFormatting = tabletTicks;
            this.margin.bottom = 50;
            this.margin.left = 70;
            this.yOffset = 0;
        }

        this.width = this.cbr.width - this.margin.left - this.margin.right;
        this.height = this.cbr.height - this.margin.top - this.margin.bottom;

        this.yScale = d3
            .scaleLinear()
            .domain([this.minVal, this.maxVal])
            .range([this.height, this.yOffset]);

        this.yAxis = d3
            .axisLeft(this.yScale)
            .tickFormat((d) => `$${d}`)
            .tickValues(generateYTicks(this.minVal, this.maxVal))
            .tickSize(0)
            .tickPadding(10);

        this.xScale = d3
            .scaleTime()
            .domain([this.start.date, this.futureDate])
            .range([0, this.width]);

        this.xAxis = d3
            .axisBottom(this.xScale)
            .tickPadding(10)
            .tickValues(this.ticks)
            .tickFormat(this.tickFormatting);

        this.svg = d3
            .select(this.chart)
            .append("svg")
            .attr("width", this.cbr.width)
            .attr("height", this.cbr.height)
            .append("g")
            .attr("transform", `translate(${this.margin.left}, ${this.margin.top})`);

        this.svg.call(this.yAxis);

        this.svg
            .append("g")
            .attr("transform", `translate(0, ${this.height})`)
            .attr("class", "x-axis")
            .call(this.xAxis);

        if (this.isTabletUp) {
            this.drawAdditionalElements();
        }

        this.drawPrice();
        this.drawLegend();
        this.drawLines();
        this.drawPriceDot();
    },

    setupEvents(add) {
        add("click", `.${RETAILER_TAB}`, this.onTabClick);

        this.setGlobalHandler("resize", _.debounce(this.onResize, 100));
    },

    setUp() {
        this.data = formatData(this.options.productHistory);
        this.createTabs();
        this.currentTab = this.el.querySelector(RETAILER_TAB_ACTIVE_SELECTOR);
        this.chart = this.el.querySelector(CHART_SELECTOR);
        this.cbr = this.chart.getBoundingClientRect();
        this.drawGraph(this.currentTab.dataset.retailer);
    },

    init() {
        this._sendEvent("price-history-graph-loaded");
        return new Promise((resolve) => {
            import(/* webpackChunkName: "d3" */ "d3").then((d3lib) => {
                // eslint-disable-line no-inline-comments
                d3 = d3lib;
                this.setUp();

                resolve();
            });
        });
    },
});
